import React from "react";
import {
    Box,
    Typography,
    Select,
    MenuItem,
    Button,
    FormControl,
    Checkbox,
    ListItemText,
    OutlinedInput,
    InputAdornment,
    IconButton,
    SelectChangeEvent,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { styled } from "./styles";
import { i18n } from "@/utils/i18n";
import { ViewType } from "../index";
import { useNavigate, useParams } from "react-router-dom";
import { Params } from "@/types/utils";

interface ActionBarProps {
    viewType: ViewType;
    onViewTypeChange: (viewType: ViewType) => void;
    selectedCategoryDescriptions: string[];
    onCategoryDescriptionChange: (descriptions: string[]) => void;
    allCategoryDescriptions: string[];
}

interface CategoryMultiSelectProps {
    value: string[];
    onChange: (value: string[]) => void;
    options: string[];
}
const CategoryMultiSelect: React.FC<CategoryMultiSelectProps> = ({ value, onChange, options }) => {
    const handleChange = (event: SelectChangeEvent<string[]>) => {
        onChange(event.target.value as string[]);
    };

    const handleClear = () => onChange([]);

    return (
        <FormControl sx={styled.categorySelect}>
            <Select
                id="category-multi-select"
                multiple
                displayEmpty
                value={value}
                onChange={handleChange}
                input={
                    <OutlinedInput
                        id="category-select-input"
                        name="category-select"
                        size="small"
                        sx={styled.selectInput}
                        endAdornment={
                            value.length > 0 ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="clear selection"
                                        onClick={handleClear}
                                        onMouseDown={e => e.preventDefault()}
                                        edge="end"
                                        size="small"
                                        sx={styled.clearButton}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null
                        }
                    />
                }
                renderValue={selected => {
                    if (!selected || (selected as string[]).length === 0) {
                        return (
                            <Typography style={styled.placeholderText}>
                                {i18n.t("selectCategories")}
                            </Typography>
                        );
                    }
                    return (selected as string[]).join(", ");
                }}
                MenuProps={{
                    PaperProps: {
                        style: { maxHeight: 48 * 7.5 + 8 },
                    },
                }}
                IconComponent={ArrowDropDownIcon}
                sx={styled.selectText}
            >
                {options.map(desc => (
                    <MenuItem key={desc} value={desc}>
                        <Checkbox
                            id={`category-checkbox-${desc}`}
                            name={`category-checkbox-${desc}`}
                            checked={value.indexOf(desc) > -1}
                            size="small"
                        />
                        <ListItemText
                            primary={desc}
                            primaryTypographyProps={{ style: styled.menuItemText }}
                        />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

const ViewTypeButton: React.FC<{
    type: "income" | "expense";
    currentView: ViewType;
    onChange: (type: ViewType) => void;
    label: string;
}> = ({ type, currentView, onChange, label }) => (
    <Button
        variant={currentView === type ? "contained" : "outlined"}
        onClick={() => onChange(currentView === type ? "all" : type)}
        sx={styled.viewTypeButton}
    >
        {label}
    </Button>
);

const ActionBar: React.FC<ActionBarProps> = ({
    viewType,
    onViewTypeChange,
    selectedCategoryDescriptions,
    onCategoryDescriptionChange,
    allCategoryDescriptions,
}) => {
    const navigate = useNavigate();
    const { oid, sid, rid } = useParams<Params>();
    return (
        <Box sx={styled.actionBarContainer}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <IconButton
                    aria-label="Go back"
                    onClick={() =>
                        navigate(`/organizations/${oid}/submissions/${sid}/reports/${rid}`)
                    }
                    sx={{ color: "text.primary" }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" fontWeight="bold">
                    {i18n.t("categorySpecification")}
                </Typography>
            </Box>

            <CategoryMultiSelect
                value={selectedCategoryDescriptions}
                onChange={onCategoryDescriptionChange}
                options={allCategoryDescriptions}
            />

            <ViewTypeButton
                type="income"
                currentView={viewType}
                onChange={onViewTypeChange}
                label={i18n.t("incomings")}
            />
            <ViewTypeButton
                type="expense"
                currentView={viewType}
                onChange={onViewTypeChange}
                label={i18n.t("outgoings")}
            />
        </Box>
    );
};

export default ActionBar;
